import * as stylex from "@stylexjs/stylex";
import { X } from "lucide-react";
import { type ReactNode, useId } from "react";
import { endForLength, type MinuteStep, pickerHours, pickerMinutes, withHour, withMeridiem, withMinute } from "@/domain/eventTimes";
import { type Clock, formatClock, formatHour, timeParts } from "@/domain/time";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "./Button";
import { Eyebrow } from "./Eyebrow";
import { MeridiemToggle } from "./MeridiemToggle";
import { press } from "./press";
type TimePickerLabels = {
  hour: string;
  minutes: string;
  meridiem: string;
  length: string;
  noEnd: string;
  done: string;
  hour24Cell: (hour: string) => string;
  minuteCell: (minutes: number) => string;
};
type LengthOption = {
  value: number;
  label: string;
};
type TimePickerEnds = {
  start: number;
  lengths: readonly LengthOption[];
  onNoEnd: () => void;
};
type TimePickerProps = {
  label: string;
  groupLabel: string;
  value: number;
  clock: Clock;
  labels: TimePickerLabels;
  onChange: (minutes: number) => void;
  onDone: () => void;
  ends?: TimePickerEnds;
  doneVariant?: "primary" | "secondary";
  minuteStep?: MinuteStep;
  compact?: boolean;
  xstyle?: stylex.StyleXStyles;
};
const reveal = "x1gpfodv-B";
const styles = {
  fieldset: {
    kMzoRj: "xc342km",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xou54vl",
    kmVPX3: "x1tamke2",
    kaIpWk: "xwe7p7i",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kKVMdj: "x1x7uuge",
    k44tkh: "x1g18oiy x1u6grsq",
    kyAemX: "xatqbsq",
    kWV6AL: "xskzprw",
    $$css: true
  },
  compactRoot: {
    kmVPX3: "xc7ga6q",
    $$css: true
  },
  grid: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x1jl941j",
    kOIVth: "x167g77z",
    $$css: true
  },
  compactGrid: {
    kumcoG: "x1sipb5g",
    $$css: true
  },
  fourColumns: {
    kumcoG: "x1sipb5g",
    $$css: true
  },
  cell: {
    kZKoxP: "xn3w4p2",
    k7Eaqz: "xeuugli",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "xh540kj",
    kMwMTN: "x1a11ngh",
    kGVxlE: "x98nf4m",
    kMv6JI: "x17jlq2w",
    kGuDYH: "xwsyq91",
    k63SB2: "x1v94iar",
    kcqcaj: "xss6m8b",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x18dpk69",
    kIyJzY: "xlc9y7w",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  lengthCell: {
    k8WAf4: "xt970qd",
    kg3NbH: "xfn1xvj",
    kGuDYH: "x19d36u7",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  selected: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    kGVxlE: "x1gnnqk1",
    k63SB2: "xfbjfs5",
    $$css: true
  },
  noEnd: {
    koQZXg: "x1bawvfr",
    $$css: true
  },
  done: {
    kGVxlE: "xnfxhx2",
    $$css: true
  }
};
type CellProps = {
  label: string;
  ariaLabel?: string;
  selected: boolean;
  onClick: () => void;
  variant?: "time" | "length";
};
type SectionProps = {
  title: string;
  children: ReactNode;
  grid?: stylex.StyleXStyles;
};
export function TimePicker({
  label,
  groupLabel,
  value,
  clock,
  labels,
  onChange,
  onDone,
  ends,
  doneVariant = "secondary",
  minuteStep = 5,
  compact,
  xstyle
}: TimePickerProps) {
  const meridiem = timeParts(value, "12h").meridiem ?? "AM";
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  const grid = compact ? styles.compactGrid : undefined;
  const minutes = pickerMinutes(minuteStep);
  const minuteGrid = minutes.length === 4 ? styles.fourColumns : grid;
  return <fieldset aria-label={groupLabel} {...stylex.props(styles.fieldset, styles.root, compact && styles.compactRoot, xstyle)}>
      <div className="x78zum5 x1qughib x6s0dn4 x1a02dak x1v2ro7d xbktkl8">
        <Eyebrow>{`${label} · ${formatClock(value, clock)}`}</Eyebrow>
        {clock === "12h" && <MeridiemToggle label={labels.meridiem} value={meridiem} onChange={next => onChange(withMeridiem(value, next))} />}
      </div>
      <Section title={labels.hour} grid={grid}>
        {pickerHours(clock, meridiem).map(h => <Cell key={h} {...hourCell(h, clock, labels)} selected={h === hour} onClick={() => onChange(withHour(value, h))} />)}
      </Section>
      <Section title={labels.minutes} grid={minuteGrid}>
        {minutes.map(m => <Cell key={m} label={String(m).padStart(2, "0")} ariaLabel={labels.minuteCell(m)} selected={m === minute} onClick={() => onChange(withMinute(value, m))} />)}
      </Section>
      {ends && <Section title={labels.length} grid={styles.fourColumns}>
          {ends.lengths.map(length => <Cell key={length.value} label={length.label} variant="length" selected={value === ends.start + length.value} onClick={() => onChange(endForLength(ends.start, length.value))} />)}
        </Section>}
      <div className="x78zum5 x1qughib x6s0dn4 x1a02dak x167g77z">
        <div>
          {ends && <Button variant="ghost" icon={X} onClick={ends.onNoEnd} xstyle={styles.noEnd}>
              {labels.noEnd}
            </Button>}
        </div>
        <Button variant={doneVariant} onClick={onDone} xstyle={doneVariant === "secondary" && styles.done}>
          {labels.done}
        </Button>
      </div>
    </fieldset>;
}
function Cell({
  label,
  ariaLabel,
  selected,
  onClick,
  variant = "time"
}: CellProps) {
  return <button type="button" aria-pressed={selected} aria-label={ariaLabel} onClick={onClick} {...stylex.props(styles.cell, variant === "length" && styles.lengthCell, selected && styles.selected, press.scale)}>
      {label}
    </button>;
}
function Section({
  title,
  children,
  grid
}: SectionProps) {
  const titleId = useId();
  return <div className="x78zum5 xdt5ytf x167g77z">
      <span id={titleId} className="xr0iwhn xhunfcg xt3et7w xtvhhri">
        {title}
      </span>
      <fieldset aria-labelledby={titleId} {...stylex.props(styles.fieldset, styles.grid, grid)}>
        {children}
      </fieldset>
    </div>;
}
function hourCell(hour: number, clock: Clock, labels: TimePickerLabels) {
  if (clock === "12h") return {
    label: String(hour % 12 || 12),
    ariaLabel: formatHour(hour * 60, clock)
  };
  const label = String(hour).padStart(2, "0");
  return {
    label,
    ariaLabel: labels.hour24Cell(label)
  };
}